<template>
  <transition name="modal-fade">
    <div v-if="modelValue" class="modal-overlay" @click.self="closeModal">
      <div class="modal-container modal-container-large">
        <div class="modal-header">
          <h2 class="modal-title">
            <IconSvg name="calendar-solid-full" size="md" />
            {{ editMode ? "スケジュール編集" : "スケジュール作成" }}
          </h2>
          <div class="modal-header-right">
            <span :class="['mode-badge', editMode ? 'mode-badge-edit' : 'mode-badge-new']">
              {{ editMode ? '更新' : '新規' }}
            </span>
            <button
              type="button"
              class="modal-close-btn"
              @click="closeModal"
              :disabled="isSubmitting"
            >
              ×
            </button>
          </div>
        </div>

        <!-- エラーメッセージ表示 -->
        <div v-if="errorMessage" class="error-message">
          {{ errorMessage }}
        </div>

        <form @submit.prevent="handleSubmit" @keydown.ctrl.enter.prevent="handleSubmit" class="modal-form">
          <!-- タイトル -->
          <div class="form-group">
            <label class="form-label">
              タイトル <span class="required">*</span>
            </label>
            <input
              v-model="formData.title"
              type="text"
              class="form-input"
              :class="{ 'input-error': errors.title }"
              placeholder="例: 松本城観光"
              maxlength="255"
              required
            />
            <p v-if="errors.title" class="error-text">
              {{ errors.title }}
            </p>
          </div>

          <!-- 開始日時 -->
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">
                開始日 <span class="required">*</span>
              </label>
              <input
                v-model="formData.startDate"
                type="date"
                class="form-input"
                :class="{ 'input-error': errors.startDate }"
                min="1900-01-01"
                max="9999-12-31"
                @input="validateYear('startDate')"
                @change="
                  validateYear('startDate');
                  handleStartDateChange();
                "
                @blur="validateYear('startDate')"
                required
              />
              <p v-if="errors.startDate" class="error-text">
                {{ errors.startDate }}
              </p>
            </div>
            <div class="form-group">
              <label class="form-label">開始時刻</label>
              <input
                v-model="formData.startTime"
                type="time"
                class="form-input"
                @change="handleStartTimeChange()"
              />
            </div>
          </div>

          <!-- 終了日時 -->
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">終了日</label>
              <input
                v-model="formData.endDate"
                type="date"
                class="form-input"
                :class="{ 'input-error': errors.endDate }"
                :min="endDateMin"
                max="9999-12-31"
                @input="validateYear('endDate')"
                @change="validateYear('endDate')"
                @blur="validateYear('endDate')"
              />
              <p v-if="errors.endDate" class="error-text">
                {{ errors.endDate }}
              </p>
            </div>
            <div class="form-group">
              <label class="form-label">終了時刻</label>
              <input
                v-model="formData.endTime"
                type="time"
                class="form-input"
                :min="endTimeMin"
              />
            </div>
          </div>

          <!-- 予定の種類 -->
          <div class="form-group">
            <label class="form-label">予定の種類</label>
            <select v-model="formData.category" class="form-input">
              <option value="">未選択</option>
              <option
                v-for="cat in activityCategories"
                :key="cat.CATEGORY_CDV"
                :value="cat.CATEGORY_CDV"
              >
                {{ cat.CATEGORY_NMV }}
              </option>
            </select>
            <p class="form-help">
              観光、食事、宿泊など予定の目的を選びます。電車やバスは下の交通手段に入力してください。
            </p>
          </div>

          <!-- 所在地 -->
          <div class="form-group map-form-group">
            <label class="form-label" for="schedule-place">
              所在地・Google Maps連携
            </label>
            <div class="address-input-row">
              <GooglePlaceInput
                v-model="formData.address"
                input-id="schedule-place"
                placeholder="施設名または住所を入力して選択"
                :maxlength="500"
                @place-selected="handlePlaceSelected"
                @manual-input="clearSelectedPlace"
              />
              <a
                v-if="googleMapsUrl"
                :href="googleMapsUrl"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Google Mapsで確認（新しいタブ）"
                class="map-confirm-link"
              >
                <IconSvg name="map-solid-full" size="sm" />
                Google Mapsで確認
              </a>
            </div>
            <p class="form-help">
              入力するとスケジュール一覧やPDFからGoogle Mapsを開けます。
            </p>
            <p v-if="errors.address" class="error-text">
              {{ errors.address }}
            </p>
          </div>

          <!-- 参考リンク -->
          <div class="form-group">
            <label class="form-label">参考リンク</label>
            <input
              v-model="formData.url"
              type="url"
              class="form-input"
              placeholder="https://example.com"
              maxlength="500"
            />
          </div>

          <!-- 交通手段 -->
          <div class="form-group">
            <label class="form-label">交通手段</label>
            <select v-model="formData.transportation" class="form-input">
              <option value="">未選択</option>
              <option
                v-for="transport in transportationChoices"
                :key="transport"
                :value="transport"
              >
                {{ transport }}
              </option>
            </select>
          </div>

          <!-- 電話番号 -->
          <div class="form-group">
            <label class="form-label">電話番号</label>
            <input
              v-model="formData.tel"
              type="tel"
              class="form-input"
              placeholder="例: 03-1234-5678"
              maxlength="50"
            />
          </div>

          <!-- メモ -->
          <div class="form-group">
            <label class="form-label">メモ</label>
            <textarea
              v-model="formData.remarks"
              class="form-input form-textarea"
              placeholder="メモを入力"
              rows="3"
              maxlength="1000"
            ></textarea>
          </div>

          <!-- 費用と通貨 -->
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">費用（全体）</label>
              <input
                v-model="displayCost"
                type="text"
                class="form-input"
                placeholder="0"
                @input="handleCostInput"
                @blur="handleCostBlur"
              />
            </div>
            <div class="form-group">
              <label class="form-label">通貨</label>
              <select
                v-model="formData.currency"
                class="form-input"
                @change="handleCurrencyChange"
              >
                <option
                  v-for="currency in currencyOptions"
                  :key="currency"
                  :value="currency"
                >
                  {{ formatCurrencyOption(currency) }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label class="form-label">通貨</label>
              <input
                type="text"
                value="JPY (日本円)"
                class="form-input"
                disabled
                readonly
              />
            </div>
          </div>

          <!-- 支払者（費用が1円以上の場合のみ表示） -->
          <div v-if="requiresRate" class="rate-panel">
            <div class="rate-summary">
              <span>1 {{ formData.currency }} =</span>
              <strong v-if="currentRate">{{ currentRate }} JPY</strong>
              <strong v-else>未設定</strong>
            </div>
            <div class="rate-controls">
              <input
                v-model="manualRate"
                type="number"
                min="0.00000001"
                step="0.00000001"
                class="form-input"
                :class="{ 'input-error': errors.rate }"
                placeholder="例: 155.12345678"
              />
              <button
                type="button"
                class="btn btn-secondary rate-btn"
                :disabled="isRateSaving"
                @click="saveManualRate"
              >
                手動設定
              </button>
              <button
                type="button"
                class="btn btn-secondary rate-btn"
                :disabled="isRateSaving"
                @click="refreshRate"
              >
                最新レート取得
              </button>
            </div>
            <p v-if="errors.rate" class="error-text">
              {{ errors.rate }}
            </p>
          </div>

          <div v-if="formData.cost && formData.cost > 0" class="form-group">
            <label class="form-label">
              支払者 <span class="required">*</span>
            </label>
            <select v-model="formData.paidBy" class="form-input">
              <option value="">選択してください</option>
              <option
                v-for="member in selectedScheduleMembers"
                :key="member.MEMBER_CODE"
                :value="member.MEMBER_CODE"
              >
                {{ member.MEMBER_NAME }}
              </option>
            </select>
            <p v-if="errors.paidBy" class="error-text">
              {{ errors.paidBy }}
            </p>
          </div>

          <!-- 参加メンバー -->
          <div class="form-group">
            <label class="form-label">
              参加メンバー <span class="required">*</span>
            </label>
            <div class="members-selection">
              <div
                v-for="member in shioriMembers"
                :key="member.MEMBER_CODE"
                class="member-item"
              >
                <label class="checkbox-label">
                  <input
                    type="checkbox"
                    :value="member.MEMBER_CODE"
                    v-model="formData.selectedMembers"
                    class="form-checkbox"
                  />
                  <span>{{ member.MEMBER_NAME }}</span>
                </label>
              </div>
            </div>
            <p v-if="errors.members" class="error-text">
              {{ errors.members }}
            </p>
          </div>

          <div v-if="formData.cost && formData.cost > 0" class="form-group">
            <label class="form-label">
              費用負担者 <span class="required">*</span>
            </label>
            <p class="form-help">
              この金額を割り勘する人だけを選択してください。
            </p>
            <div class="members-selection">
              <div
                v-for="member in selectedScheduleMembers"
                :key="`burden-${member.MEMBER_CODE}`"
                class="member-item"
              >
                <label class="checkbox-label">
                  <input
                    type="checkbox"
                    :value="member.MEMBER_CODE"
                    v-model="formData.burdenMemberCodes"
                    class="form-checkbox"
                  />
                  <span>{{ member.MEMBER_NAME }}</span>
                </label>
              </div>
            </div>
            <p v-if="errors.burdenMembers" class="error-text">
              {{ errors.burdenMembers }}
            </p>
          </div>

          <!-- 旅程への表示・予約状況 -->
          <div class="form-group">
            <label class="form-label checkbox-label">
              <input
                v-model="formData.needFlg"
                type="checkbox"
                class="form-checkbox"
              />
              <span>予定を確定</span>
            </label>
            <p class="form-help checkbox-help">
              オフにすると未確定の仮予定として管理できます。
            </p>
          </div>

          <div class="form-group">
            <label class="form-label checkbox-label">
              <input
                v-model="formData.reservation"
                type="checkbox"
                class="form-checkbox"
              />
              <span>予約が必要</span>
            </label>
          </div>

          <div v-if="formData.reservation" class="form-group form-group-indent">
            <label class="form-label checkbox-label">
              <input
                v-model="formData.reservationDone"
                type="checkbox"
                class="form-checkbox"
              />
              <span>予約完了</span>
            </label>
          </div>

          <div v-if="formData.reservation" class="reservation-document-fields">
            <div class="form-group">
              <label class="form-label">予約書類・リンク名</label>
              <input
                v-model="formData.reservationDocumentName"
                type="text"
                class="form-input"
                placeholder="例: ホテル予約確認書"
                maxlength="255"
              />
            </div>
            <div class="form-group">
              <label class="form-label">予約書類URL</label>
              <input
                v-model="formData.reservationDocumentUrl"
                type="url"
                class="form-input"
                placeholder="https://example.com/reservation"
                maxlength="500"
              />
            </div>
          </div>

          <div class="modal-footer">
            <button
              type="button"
              @click="closeModal"
              class="btn btn-secondary"
              :disabled="isSubmitting"
            >
              キャンセル
            </button>
            <button
              type="submit"
              class="btn btn-primary"
              :disabled="isSubmitting"
            >
              {{
                isSubmitting
                  ? editMode
                    ? "更新中..."
                    : "作成中..."
                  : editMode
                  ? "更新"
                  : "作成"
              }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </transition>
</template>

<script>
import { ref, watch, computed, onMounted } from "vue";
import IconSvg from "@/components/IconSvg.vue";
import GooglePlaceInput from "@/components/GooglePlaceInput.vue";
import { useScheduleStore } from "@/store/schedule-store.js";
import { useShioriStore } from "@/store/shiori-store.js";
import { useShioriMemberStore } from "@/store/shiori-member-store.js";
import { useScheduleCategoryStore } from "@/store/schedule-category-store.js";
import { useWarikanStore } from "@/store/warikan-store.js";
import {
  buildCurrencyOptions,
  formatCurrencyOption,
  getRateToJpy,
  normalizeCurrency,
} from "@/utils/exchange-rate";

export default {
  name: "CreateScheduleModal",
  components: {
    IconSvg,
    GooglePlaceInput,
  },
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    shioriId: {
      type: String,
      required: true,
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    scheduleData: {
      type: Object,
      default: null,
    },
    initialDate: {
      type: String,
      default: null,
    },
    prefillData: {
      type: Object,
      default: null,
    },
    shareToken: {
      type: String,
      default: "",
    },
    members: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["update:modelValue", "create", "update", "access-denied"],
  setup(props, { emit }) {
    const scheduleStore = useScheduleStore();
    const shioriStore = useShioriStore();
    const shioriMemberStore = useShioriMemberStore();
    const scheduleCategoryStore = useScheduleCategoryStore();
    const warikanStore = useWarikanStore();
    const transportationCategoryCodes = new Set([
      "car",
      "train",
      "plane",
      "bus",
      "ship",
    ]);
    const transportationOptions = [
      "徒歩",
      "電車",
      "新幹線",
      "バス",
      "車",
      "タクシー",
      "飛行機",
      "船",
      "自転車",
    ];

    const isDisplayableCategory = (category) => {
      const code = String(category?.CATEGORY_CDV || "").trim();
      const name = String(category?.CATEGORY_NMV || "").trim();
      const hasInvalidChar = [...name].some((char) => {
        const codePoint = char.codePointAt(0);
        return codePoint === 0xfffd || codePoint < 0x20 || codePoint === 0x7f;
      });
      return Boolean(
        code &&
          name &&
          !hasInvalidChar,
      );
    };

    const normalizeCategory = (category) => ({
      ...category,
      CATEGORY_CDV: String(category.CATEGORY_CDV).trim(),
      CATEGORY_NMV: String(category.CATEGORY_NMV).trim(),
    });

    const uniqueCategoriesByCode = (categories) => {
      const categoryMap = new Map();
      categories.forEach((category) => {
        if (!categoryMap.has(category.CATEGORY_CDV)) {
          categoryMap.set(category.CATEGORY_CDV, category);
        }
      });
      return Array.from(categoryMap.values());
    };

    onMounted(async () => {
      await scheduleCategoryStore.fetchCategories();
    });

    // しおりメンバーを取得
    const shioriMembers = computed(() => {
      if (props.shareToken) {
        return props.members;
      }
      return shioriMemberStore.members[props.shioriId] || [];
    });

    const selectedScheduleMembers = computed(() => {
      return shioriMembers.value.filter((member) =>
        formData.value.selectedMembers.includes(member.MEMBER_CODE),
      );
    });

    const selectedCurrency = computed(() =>
      normalizeCurrency(formData.value.currency),
    );

    const requiresRate = computed(() => selectedCurrency.value !== "JPY");

    const currentRate = computed(() =>
      getRateToJpy(selectedCurrency.value, warikanStore.exchangeRateMap),
    );

    const activityCategories = computed(() => {
      const categories = uniqueCategoriesByCode(scheduleCategoryStore.categoryList
        .filter(isDisplayableCategory)
        .map(normalizeCategory)
        .filter(
          (category) =>
            !transportationCategoryCodes.has(category.CATEGORY_CDV),
        ));
      if (!categories.some((category) => category.CATEGORY_CDV === "movement")) {
        categories.unshift({
          CATEGORY_CDV: "movement",
          CATEGORY_NMV: "移動",
          ICON_NMV: "train-subway-solid-full",
        });
      }
      const selected = scheduleCategoryStore.categoryList
        .filter(isDisplayableCategory)
        .map(normalizeCategory)
        .find((category) => category.CATEGORY_CDV === formData.value.category);
      if (
        selected &&
        transportationCategoryCodes.has(selected.CATEGORY_CDV)
      ) {
        return [selected, ...categories];
      }
      return categories;
    });

    const transportationChoices = computed(() => {
      const choices = [...transportationOptions];
      const current = formData.value.transportation?.trim();
      if (current && !choices.includes(current)) {
        choices.unshift(current);
      }
      return choices;
    });

    const googleMapsUrl = computed(() => {
      const placeName = normalizePlaceText(formData.value.googlePlaceName);
      const placeId = normalizePlaceText(formData.value.googlePlaceId);
      const address = normalizePlaceText(formData.value.address);
      const query = placeName || address;
      if (!query) return "";
      const params = new URLSearchParams({ api: "1", query });
      if (placeName && placeId) params.set("query_place_id", placeId);
      return `https://www.google.com/maps/search/?${params.toString()}`;
    });

    // 現在のしおりデータを取得
    const currentShiori = computed(() => {
      return shioriStore.shiori.find(
        (item) => item.SHIORI_NOV === props.shioriId,
      );
    });

    // 終了日の最小値（開始日または1900-01-01）
    const endDateMin = computed(() => {
      return formData.value.startDate || "1900-01-01";
    });

    // 終了時刻の最小値（開始日と終了日が同じ場合のみ開始時刻、それ以外は制限なし）
    const endTimeMin = computed(() => {
      // 終了日が開始日と同じ場合のみ、開始時刻以降に制限
      if (
        formData.value.startDate &&
        formData.value.endDate &&
        formData.value.startDate === formData.value.endDate &&
        formData.value.startTime
      ) {
        return formData.value.startTime;
      }
      return null; // 制限なし
    });

    const formData = ref({
      title: "",
      startDate: "",
      startTime: "",
      endDate: "",
      endTime: "",
      timeDifference: "9",
      category: "",
      url: "",
      transportation: "",
      tel: "",
      address: "",
      googlePlaceName: "",
      googlePlaceId: "",
      latitude: null,
      longitude: null,
      remarks: "",
      cost: null,
      currency: "JPY",
      reservation: false,
      needFlg: true,
      reservationDone: false,
      reservationDocumentName: "",
      reservationDocumentUrl: "",
      paidBy: "", // 支払者のメンバーコード
      selectedMembers: [],
      burdenMemberCodes: [],
    });

    const selectedPlaceAddress = ref("");
    const normalizePlaceText = (value) => String(value || "").trim();
    const nullableText = (value) => {
      if (value === null || value === undefined) return null;
      if (typeof value !== "string") return value;
      const trimmedValue = value.trim();
      return trimmedValue === "" ? null : trimmedValue;
    };
    const nullableFiniteNumber = (value) => {
      if (value === null || value === undefined || value === "") return null;
      const numberValue = Number(value);
      return Number.isFinite(numberValue) ? numberValue : null;
    };

    const handlePlaceSelected = (place) => {
      formData.value.address = place.address || formData.value.address;
      formData.value.googlePlaceName = normalizePlaceText(place.name);
      formData.value.googlePlaceId = normalizePlaceText(place.placeId);
      const latitude = Number(place.latitude);
      const longitude = Number(place.longitude);
      formData.value.latitude = Number.isFinite(latitude) ? latitude : null;
      formData.value.longitude = Number.isFinite(longitude) ? longitude : null;
      selectedPlaceAddress.value = normalizePlaceText(formData.value.address);
    };

    const clearSelectedPlace = (value = formData.value.address) => {
      if (
        selectedPlaceAddress.value &&
        normalizePlaceText(value) === selectedPlaceAddress.value
      ) {
        return;
      }
      formData.value.latitude = null;
      formData.value.longitude = null;
      formData.value.googlePlaceName = "";
      formData.value.googlePlaceId = "";
      selectedPlaceAddress.value = "";
    };

    // 費用の表示用フィールド（カンマ区切り）
    const displayCost = ref("");
    const manualRate = ref("");
    const isRateSaving = ref(false);
    const currencyOptions = computed(() =>
      buildCurrencyOptions(formData.value.currency),
    );

    // 費用をカンマ区切りでフォーマット
    const formatCostWithComma = (value) => {
      if (!value) return "";
      // 数値に変換してカンマ区切りに
      return new Intl.NumberFormat("ja-JP").format(value);
    };

    // カンマを除去して数値に変換
    const parseCostFromDisplay = (value) => {
      if (!value) return null;
      // カンマを除去して数値化
      const parsed = parseFloat(value.replace(/,/g, ""));
      return isNaN(parsed) ? null : parsed;
    };

    // 表示用費用の入力処理
    const handleCostInput = (event) => {
      const pattern = requiresRate.value ? /[^0-9.]/g : /[^0-9]/g;
      let inputValue = event.target.value.replace(pattern, "");
      if (requiresRate.value) {
        const parts = inputValue.split(".");
        inputValue = `${parts[0]}${
          parts.length > 1 ? `.${parts.slice(1).join("").slice(0, 2)}` : ""
        }`;
      }
      displayCost.value = inputValue;
      // カンマを除去して数値を取得
      const numericValue = parseCostFromDisplay(inputValue);
      formData.value.cost =
        numericValue === null
          ? null
          : requiresRate.value
          ? Number(numericValue.toFixed(2))
          : Math.round(numericValue);
    };

    // 表示用費用のフォーカス喪失時にフォーマット
    const handleCostBlur = () => {
      if (formData.value.cost) {
        displayCost.value = requiresRate.value
          ? Number(formData.value.cost).toLocaleString("ja-JP", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
          : formatCostWithComma(formData.value.cost);
      } else {
        displayCost.value = "";
      }
    };

    const errors = ref({
      title: "",
      startDate: "",
      startTime: "",
      endDate: "",
      members: "",
      paidBy: "",
      burdenMembers: "",
      address: "",
      rate: "",
    });

    const errorMessage = ref("");
    const isSubmitting = ref(false);

    const closeModal = () => {
      emit("update:modelValue", false);
      // モーダルを閉じるときにフォームをリセット
      setTimeout(() => {
        resetForm();
      }, 300); // アニメーション後にリセット
    };

    const clearErrors = () => {
      errors.value = {
        title: "",
        startDate: "",
        startTime: "",
        endDate: "",
        members: "",
        paidBy: "",
        burdenMembers: "",
        address: "",
        rate: "",
      };
      errorMessage.value = "";
    };

    const validateYear = (fieldName) => {
      const dateValue = formData.value[fieldName];
      if (!dateValue) return;

      // 年の部分を抽出して検証（YYYY-MM-DD形式）
      const yearMatch = dateValue.match(/^(\d+)-/);
      if (yearMatch) {
        const year = yearMatch[1];

        // 年が3桁以下の場合は編集中と判断して何もしない
        if (year.length < 4) {
          return;
        }

        // 年が4桁を超える場合のみ、最初の4桁に切り詰め
        if (year.length > 4) {
          const validYear = year.substring(0, 4);
          const [, month, day] = dateValue.split("-");
          formData.value[fieldName] = `${validYear}-${month || "01"}-${
            day || "01"
          }`;
          return;
        }

        // 年が正確に4桁の場合のみ範囲チェック
        if (year.length === 4) {
          const yearNum = parseInt(year);
          if (yearNum < 1900) {
            formData.value[fieldName] = `1900-${
              dateValue.split("-")[1] || "01"
            }-${dateValue.split("-")[2] || "01"}`;
          } else if (yearNum > 9999) {
            formData.value[fieldName] = `9999-${
              dateValue.split("-")[1] || "12"
            }-${dateValue.split("-")[2] || "31"}`;
          }
        }
      }
    };

    // 開始日が変更された時の処理
    const handleStartDateChange = () => {
      const startDate = formData.value.startDate;

      if (startDate) {
        if (formData.value.startTime && !formData.value.endTime) {
          setDefaultEndTime();
        } else if (!formData.value.endDate && !formData.value.endTime) {
          formData.value.endDate = startDate;
        }
      }
    };

    const setDefaultEndTime = () => {
      const { startDate, startTime } = formData.value;
      if (!startDate || !startTime) return;

      const [hours, minutes] = startTime.split(":").map(Number);
      const end = new Date(`${startDate}T00:00:00`);
      end.setHours(hours + 1, minutes, 0, 0);

      const year = end.getFullYear();
      const month = String(end.getMonth() + 1).padStart(2, "0");
      const day = String(end.getDate()).padStart(2, "0");
      const endHours = String(end.getHours()).padStart(2, "0");
      const endMinutes = String(end.getMinutes()).padStart(2, "0");

      formData.value.endDate = `${year}-${month}-${day}`;
      formData.value.endTime = `${endHours}:${endMinutes}`;
    };

    // 開始時刻が変更された時の処理
    const handleStartTimeChange = () => {
      if (!formData.value.startTime) {
        formData.value.endDate = "";
        formData.value.endTime = "";
        return;
      }
      if (!formData.value.endTime) {
        setDefaultEndTime();
      }
    };

    const validateForm = () => {
      clearErrors();
      let isValid = true;

      // タイトルのバリデーション
      if (!formData.value.title || !formData.value.title.trim()) {
        errors.value.title = "タイトルは必須です";
        isValid = false;
      } else if (formData.value.title.length > 255) {
        errors.value.title = "タイトルは255文字以内で入力してください";
        isValid = false;
      }

      // 開始日のバリデーション
      if (!formData.value.startDate) {
        errors.value.startDate = "開始日は必須です";
        isValid = false;
      } else if (currentShiori.value) {
        // 旅行期間内かチェック
        const startDate = new Date(formData.value.startDate);
        const tripFrom = new Date(currentShiori.value.TRIP_FROM);
        const tripTo = new Date(currentShiori.value.TRIP_TO);

        if (startDate < tripFrom || startDate > tripTo) {
          errors.value.startDate = `旅行期間（${formatDateShort(
            currentShiori.value.TRIP_FROM,
          )}〜${formatDateShort(
            currentShiori.value.TRIP_TO,
          )}）内の日付を選択してください`;
          isValid = false;
        }
      }

      // 終了日のバリデーション（入力されている場合のみ）
      if (formData.value.endDate) {
        if (formData.value.startDate) {
          const start = new Date(formData.value.startDate);
          const end = new Date(formData.value.endDate);
          if (end < start) {
            errors.value.endDate = "終了日は開始日以降の日付を指定してください";
            isValid = false;
          }
        }

        // 旅行期間内かチェック
        if (currentShiori.value) {
          const endDate = new Date(formData.value.endDate);
          const tripFrom = new Date(currentShiori.value.TRIP_FROM);
          const tripTo = new Date(currentShiori.value.TRIP_TO);

          if (endDate < tripFrom || endDate > tripTo) {
            errors.value.endDate = `旅行期間（${formatDateShort(
              currentShiori.value.TRIP_FROM,
            )}〜${formatDateShort(
              currentShiori.value.TRIP_TO,
            )}）内の日付を選択してください`;
            isValid = false;
          }
        }
      }

      if (formData.value.selectedMembers.length === 0) {
        errors.value.members = "少なくとも1人のメンバーを選択してください";
        isValid = false;
      }

      // 支払者のバリデーション（費用が1円以上の場合）
      if (formData.value.cost && formData.value.cost > 0) {
        if (
          selectedCurrency.value === "JPY" &&
          !Number.isInteger(formData.value.cost)
        ) {
          errors.value.rate = "JPYは整数で入力してください";
          isValid = false;
        }
        if (requiresRate.value && !currentRate.value) {
          errors.value.rate = "保存前に為替レートを設定してください";
          isValid = false;
        }
        if (!formData.value.paidBy) {
          errors.value.paidBy = "支払者を選択してください";
          isValid = false;
        } else if (
          !formData.value.selectedMembers.includes(formData.value.paidBy)
        ) {
          errors.value.paidBy = "支払者は参加メンバーから選択してください";
          isValid = false;
        }
        const selectedBurdenCount = formData.value.burdenMemberCodes.filter(
          (memberCode) =>
            formData.value.selectedMembers.includes(memberCode),
        ).length;
        if (selectedBurdenCount === 0) {
          errors.value.burdenMembers =
            "費用負担者を1人以上選択してください";
          isValid = false;
        }
      }

      return isValid;
    };

    const handleCurrencyChange = () => {
      formData.value.currency = selectedCurrency.value;
      if (formData.value.cost) {
        formData.value.cost =
          selectedCurrency.value === "JPY"
            ? Math.round(formData.value.cost)
            : Number(formData.value.cost.toFixed(2));
        handleCostBlur();
      }
      errors.value.rate = "";
    };

    const saveManualRate = async () => {
      const rate = Number(manualRate.value);
      if (!Number.isFinite(rate) || rate <= 0) {
        errors.value.rate = "正の為替レートを入力してください";
        return false;
      }
      isRateSaving.value = true;
      errors.value.rate = "";
      try {
        if (props.shareToken) {
          await warikanStore.upsertSharedExchangeRate(
            props.shareToken,
            selectedCurrency.value,
            rate,
          );
        } else {
          await warikanStore.upsertExchangeRate(
            props.shioriId,
            selectedCurrency.value,
            rate,
          );
        }
        manualRate.value = "";
        return true;
      } catch (error) {
        errors.value.rate =
          error.response?.data?.detail || "為替レートの保存に失敗しました";
        if (
          props.shareToken &&
          [403, 404].includes(error.response?.status)
        ) {
          emit("access-denied", error);
        }
        return false;
      } finally {
        isRateSaving.value = false;
      }
    };

    const refreshRate = async () => {
      isRateSaving.value = true;
      errors.value.rate = "";
      try {
        if (props.shareToken) {
          await warikanStore.refreshSharedExchangeRates(props.shareToken, [
            selectedCurrency.value,
          ]);
        } else {
          await warikanStore.refreshExchangeRates(props.shioriId, [
            selectedCurrency.value,
          ]);
        }
      } catch (error) {
        errors.value.rate =
          error.response?.data?.detail || "最新レートの取得に失敗しました";
        if (
          props.shareToken &&
          [403, 404].includes(error.response?.status)
        ) {
          emit("access-denied", error);
        }
      } finally {
        isRateSaving.value = false;
      }
    };

    const formatDateShort = (dateString) => {
      if (!dateString) return "";
      const date = new Date(dateString);
      const month = date.getMonth() + 1;
      const day = date.getDate();
      return `${month}/${day}`;
    };

    const buildSchedulePayload = () => ({
      title: formData.value.title,
      startDate: formData.value.startDate,
      startTime: nullableText(formData.value.startTime),
      endDate: nullableText(formData.value.endDate),
      endTime: nullableText(formData.value.endTime),
      timeDifference: nullableText(formData.value.timeDifference),
      category: nullableText(formData.value.category),
      url: nullableText(formData.value.url),
      transportation: nullableText(formData.value.transportation),
      tel: nullableText(formData.value.tel),
      address: normalizePlaceText(formData.value.address) || null,
      googlePlaceNmv:
        normalizePlaceText(formData.value.googlePlaceName) || null,
      googlePlaceId:
        normalizePlaceText(formData.value.googlePlaceId) || null,
      latitude: nullableFiniteNumber(formData.value.latitude),
      longitude: nullableFiniteNumber(formData.value.longitude),
      remarks: nullableText(formData.value.remarks),
      cost: nullableFiniteNumber(formData.value.cost),
      currency: nullableText(formData.value.currency),
      reservation: formData.value.reservation,
      needFlg: formData.value.needFlg,
      reservationDone: formData.value.reservationDone,
      reservationDocumentName: nullableText(
        formData.value.reservationDocumentName,
      ),
      reservationDocumentUrl: nullableText(
        formData.value.reservationDocumentUrl,
      ),
      paidBy: nullableText(formData.value.paidBy),
    });

    const handleSubmit = async () => {
      if (!validateForm()) {
        errorMessage.value = "入力内容に誤りがあります。修正してください。";
        return;
      }

      isSubmitting.value = true;
      errorMessage.value = "";

      try {
        const schedulePayload = buildSchedulePayload();

        if (props.editMode && props.scheduleData) {
          // 編集モード - スケジュール更新
          // メンバー情報も含める
          const members = formData.value.selectedMembers.map((memberCode) => ({
            member_code: memberCode,
            split_participate_flg: formData.value.burdenMemberCodes.includes(
              memberCode,
            )
              ? "1"
              : "0",
          }));

          const updateData = {
            ...schedulePayload,
            shioriId: props.shioriId,
            members: members,
          };

          const result = await scheduleStore.updateSchedule(
            props.shioriId,
            props.scheduleData.LINE_NON,
            updateData,
            { silent: true },
            props.shareToken,
          );
          emit("update", result);
        } else {
          // 新規作成モード
          // 選択されたメンバーを登録
          const members = formData.value.selectedMembers.map((memberCode) => ({
            member_code: memberCode,
            split_participate_flg: formData.value.burdenMemberCodes.includes(
              memberCode,
            )
              ? "1"
              : "0",
          }));

          const scheduleData = {
            ...schedulePayload,
            shioriId: props.shioriId,
            members: members,
          };

          const result = await scheduleStore.createSchedule(
            scheduleData,
            props.shareToken,
          );
          emit("create", result);
        }

        // フォームをリセットしてモーダルを閉じる
        resetForm();
        closeModal();
      } catch (error) {
        console.error("スケジュール処理に失敗しました。");
        if (
          props.shareToken &&
          [403, 404].includes(error.response?.status)
        ) {
          emit("access-denied", error);
        }

        // エラーメッセージを表示
        if (error.response?.data?.detail) {
          if (Array.isArray(error.response.data.detail)) {
            errorMessage.value = error.response.data.detail
              .map((err) => err.msg)
              .join(", ");
          } else {
            errorMessage.value = error.response.data.detail;
          }
        } else {
          errorMessage.value = props.editMode
            ? "スケジュールの更新に失敗しました。もう一度お試しください。"
            : "スケジュールの作成に失敗しました。もう一度お試しください。";
        }
      } finally {
        isSubmitting.value = false;
      }
    };

    const resetForm = () => {
      formData.value = {
        title: "",
        startDate: "",
        startTime: "",
        endDate: "",
        endTime: "",
        timeDifference: "9",
        category: "",
        url: "",
        transportation: "",
        tel: "",
        address: "",
        googlePlaceName: "",
        googlePlaceId: "",
        latitude: null,
        longitude: null,
        remarks: "",
        cost: null,
        currency: "JPY",
        reservation: false,
        needFlg: true,
        reservationDone: false,
        reservationDocumentName: "",
        reservationDocumentUrl: "",
        paidBy: "",
        selectedMembers: [], // メンバー選択をクリア
        burdenMemberCodes: [],
      };
      selectedPlaceAddress.value = "";
      clearErrors();
    };

    // モーダルが開かれたときにメンバーを取得
    watch(
      () => props.modelValue,
      async (isOpen) => {
        if (isOpen && props.shioriId) {
          let exchangeRateLoadError = "";
          if (props.shareToken) {
            try {
              await warikanStore.fetchSharedExchangeRates(props.shareToken);
            } catch (error) {
              if ([403, 404].includes(error.response?.status)) {
                emit("access-denied", error);
                return;
              }
              exchangeRateLoadError =
                error.response?.data?.detail ||
                "為替レートの取得に失敗しました。もう一度お試しください。";
            }
          } else {
            await warikanStore.fetchExchangeRates(props.shioriId);
          }

          console.log(
            "[CreateScheduleModal] モーダルが開かれました。しおりID:",
            props.shioriId,
            "editMode:",
            props.editMode,
          );

          // 新規作成モードの場合、フォームをリセット
          if (!props.editMode) {
            console.log(
              "[CreateScheduleModal] 新規作成モード - フォームをリセット",
            );
            resetForm();

            // 行きたいとこリストからプリフィル
            if (props.prefillData) {
              formData.value.title = props.prefillData.title || "";
              if (props.prefillData.startDate)
                formData.value.startDate = props.prefillData.startDate;
              if (props.prefillData.startTime)
                formData.value.startTime = props.prefillData.startTime;
              if (props.prefillData.endDate)
                formData.value.endDate = props.prefillData.endDate;
              if (props.prefillData.endTime)
                formData.value.endTime = props.prefillData.endTime;
              if (props.prefillData.category)
                formData.value.category = props.prefillData.category;
              if (props.prefillData.address)
                formData.value.address = props.prefillData.address;
              if (props.prefillData.googlePlaceNmv)
                formData.value.googlePlaceName =
                  props.prefillData.googlePlaceNmv;
              if (props.prefillData.googlePlaceId)
                formData.value.googlePlaceId = props.prefillData.googlePlaceId;
              if (props.prefillData.latitude !== undefined)
                formData.value.latitude = props.prefillData.latitude;
              if (props.prefillData.longitude !== undefined)
                formData.value.longitude = props.prefillData.longitude;
              if (props.prefillData.url)
                formData.value.url = props.prefillData.url;
              if (props.prefillData.transportation)
                formData.value.transportation =
                  props.prefillData.transportation;
              if (props.prefillData.remarks)
                formData.value.remarks = props.prefillData.remarks;
              if (props.prefillData.reservation !== undefined)
                formData.value.reservation = Boolean(
                  props.prefillData.reservation,
                );
              if (props.prefillData.reservationDone !== undefined)
                formData.value.reservationDone = Boolean(
                  props.prefillData.reservationDone,
                );
              if (props.prefillData.reservationDocumentName)
                formData.value.reservationDocumentName =
                  props.prefillData.reservationDocumentName;
              if (props.prefillData.reservationDocumentUrl)
                formData.value.reservationDocumentUrl =
                  props.prefillData.reservationDocumentUrl;
              if (props.prefillData.needFlg !== undefined)
                formData.value.needFlg = Boolean(props.prefillData.needFlg);
              if (props.prefillData.cost !== null && props.prefillData.cost !== undefined) {
                formData.value.cost = Number(props.prefillData.cost);
                displayCost.value = Number(
                  props.prefillData.cost,
                ).toLocaleString();
              }
              if (props.prefillData.currency)
                formData.value.currency = props.prefillData.currency;
            }

            // 初期日付が指定されている場合は設定
            if (props.initialDate) {
              console.log(
                "[CreateScheduleModal] 初期日付を設定:",
                props.initialDate,
              );
              formData.value.startDate = props.initialDate;
              formData.value.endDate = props.initialDate;
            }
          }

          // メンバーを取得
          if (!props.shareToken) {
            await shioriMemberStore.fetchMembers(props.shioriId);
          }
          console.log(
            "[CreateScheduleModal] メンバー取得完了:",
            shioriMembers.value,
          );

          // 新規作成モードの場合、デフォルトで全員を選択
          if (!props.editMode) {
            formData.value.selectedMembers = shioriMembers.value.map(
              (m) => m.MEMBER_CODE,
            );
            formData.value.burdenMemberCodes = [
              ...formData.value.selectedMembers,
            ];
            console.log(
              "[CreateScheduleModal] デフォルトで全員選択:",
              formData.value.selectedMembers,
            );
          }

          if (exchangeRateLoadError) {
            errors.value.rate = exchangeRateLoadError;
            errorMessage.value = exchangeRateLoadError;
          }
        }
      },
    );
    // 編集・コピーモードの時、scheduleDataが変更されたらフォームに反映
    watch(
      () => props.scheduleData,
      (newData) => {
        // scheduleDataが存在する場合（編集モードまたはコピーモード）にフォームに反映
        if (newData) {
          const mode = props.editMode ? "編集モード" : "コピーモード";
          console.log(`[CreateScheduleModal] ${mode} - データ反映開始`);
          // 電話番号を結合
          const tel = [
            newData.TEL_AREA_CODE,
            newData.TEL_CITY_CODE,
            newData.TEL_SUBSCRIBER_NUMBER,
          ]
            .filter(Boolean)
            .join("-");

          // 住所を結合
          const address = [
            newData.ZIP_CODE1 && newData.ZIP_CODE2
              ? `〒${newData.ZIP_CODE1}-${newData.ZIP_CODE2}`
              : "",
            newData.PREFECTURE,
            newData.CITY,
            newData.TOWN,
            newData.ADDRESS_NMV,
            newData.BUILDING,
          ]
            .filter(Boolean)
            .join(" ");

          // 参加者と費用負担者をそれぞれ復元
          console.log(
            `[CreateScheduleModal] ${mode} - メンバー情報:`,
            newData.MEMBERS,
          );
          const selectedMemberCodes = newData.MEMBERS
            ? newData.MEMBERS.map((m) => m.MEMBER_CODE)
            : [];
          const burdenMemberCodes = newData.MEMBERS
            ? newData.MEMBERS.filter(
                (m) => m.SPLIT_PARTICIPATE_FLG === "1",
              ).map((m) => m.MEMBER_CODE)
            : [];

          // 支払者を特定（PAID_AMOUNTが費用と一致するメンバー）
          const paidByMember = newData.MEMBERS
            ? newData.MEMBERS.find(
                (m) =>
                  m.PAID_AMOUNT &&
                  Number(m.PAID_AMOUNT) === Number(newData.COST),
              )
            : null;

          formData.value = {
            title: newData.SUBJECT_NMV || "",
            startDate: newData.START_YMD || "",
            startTime: newData.START_TIME || "",
            endDate: newData.END_YMD || "",
            endTime: newData.END_TIME || "",
            timeDifference: "9", // 固定値（日本標準時）
            category: newData.CATEGORY_CDV || "",
            url: newData.URL_NMV || "",
            transportation: newData.TRANSPORTATION_NMV || "",
            tel: tel,
            address: address,
            googlePlaceName:
              newData.GOOGLE_PLACE_NMV || newData.googlePlaceNmv || "",
            googlePlaceId:
              newData.GOOGLE_PLACE_ID || newData.googlePlaceId || "",
            latitude: newData.LATITUDE ?? null,
            longitude: newData.LONGITUDE ?? null,
            remarks: newData.REMARKS || "",
            cost: newData.COST || null,
            currency: newData.CURRENCY || "JPY",
            reservation: newData.RESERVATION_FLG === "1",
            needFlg: newData.NEED_FLG !== "0",
            reservationDone: newData.RESERVATION_DONE_FLG === "1",
            reservationDocumentName:
              newData.RESERVATION_DOCUMENT_NAME || "",
            reservationDocumentUrl:
              newData.RESERVATION_DOCUMENT_URL || "",
            paidBy: paidByMember ? paidByMember.MEMBER_CODE : "",
            selectedMembers: selectedMemberCodes,
            burdenMemberCodes,
          };
          selectedPlaceAddress.value =
            newData.LATITUDE !== null && newData.LATITUDE !== undefined &&
            newData.LONGITUDE !== null && newData.LONGITUDE !== undefined
              ? normalizePlaceText(address)
              : "";

          // 編集データの日付を検証
          if (formData.value.startDate) {
            validateYear("startDate");
          }
          if (formData.value.endDate) {
            validateYear("endDate");
          }

          console.log(
            `[CreateScheduleModal] ${mode} - 選択されたメンバー:`,
            selectedMemberCodes,
          );

          // 表示用費用を初期化
          if (newData.COST) {
            displayCost.value = formatCostWithComma(newData.COST);
          } else {
            displayCost.value = "";
          }
        }
      },
      { immediate: true },
    );

    // 新規作成モードで初期日付が変更された時にフォームに反映（モーダルが開いている間）
    watch(
      () => props.initialDate,
      (newDate) => {
        // モーダルが開いていて、新規作成モードかつinitialDateが存在する場合のみ反映
        if (props.modelValue && newDate && !props.editMode) {
          console.log("[CreateScheduleModal] 初期日付を更新:", newDate);
          formData.value.startDate = newDate;
          formData.value.endDate = newDate;
        }
      },
    );

    return {
      formData,
      errors,
      errorMessage,
      isSubmitting,
      isRateSaving,
      manualRate,
      currencyOptions,
      formatCurrencyOption,
      shioriMembers,
      selectedScheduleMembers,
      requiresRate,
      currentRate,
      activityCategories,
      transportationOptions,
      transportationChoices,
      googleMapsUrl,
      displayCost,
      endDateMin,
      endTimeMin,
      scheduleCategoryStore,
      closeModal,
      handleSubmit,
      validateYear,
      handleStartDateChange,
      handleStartTimeChange,
      handlePlaceSelected,
      clearSelectedPlace,
      handleCostInput,
      handleCostBlur,
      handleCurrencyChange,
      saveManualRate,
      refreshRate,
    };
  },
};
</script>

<style scoped>
/* モーダルオーバーレイ */
.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
  padding-top: 5rem;
  overflow-y: auto;
}

/* モーダルコンテナ */
.modal-container {
  position: relative;
  z-index: 10001;
  background: white;
  border-radius: var(--radius-xl);
  width: 100%;
  max-width: 28rem;
  box-shadow: var(--shadow-xl);
  animation: modal-slide-up 0.3s ease-out;
  overflow: hidden;
}

.modal-container-large {
  max-width: 42rem;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

@keyframes modal-slide-up {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* モーダルヘッダー */
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid var(--color-border-light);
  flex-shrink: 0;
}

.modal-header-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.mode-badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.625rem;
  border-radius: var(--radius-sm);
  letter-spacing: 0.025em;
}

.mode-badge-new {
  background-color: #d1fae5;
  color: #065f46;
}

.mode-badge-edit {
  background-color: #fef3c7;
  color: #92400e;
}

.modal-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--color-text);
  margin: 0;
}

.modal-close-btn {
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  font-size: 1.75rem;
  line-height: 1;
  cursor: pointer;
  border-radius: var(--radius-md);
  transition: all var(--transition-base);
}

.modal-close-btn:hover {
  background-color: var(--color-cool-bg);
  color: var(--color-text);
}

/* フォーム */
.modal-form {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}

.form-group {
  margin-bottom: 1.25rem;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.form-row .form-group:has(.form-input[disabled][readonly]) {
  display: none;
}

.form-row-3 {
  grid-template-columns: repeat(3, 1fr);
}

.form-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 0.5rem;
}

.form-help {
  margin: -0.25rem 0 0.75rem;
  color: var(--color-text-muted);
  font-size: 0.8rem;
}

select + .form-help,
.address-input-row + .form-help {
  margin-top: 0.4rem;
  margin-bottom: 0;
}

.map-form-group {
  padding: 0.9rem;
  border: 1px solid #bfdbfe;
  border-radius: var(--radius-md);
  background: #eff6ff;
}

.checkbox-help {
  margin: 0.35rem 0 0 1.625rem;
}

.required {
  color: #ef4444;
}

.form-input {
  width: 100%;
  padding: 0.625rem 0.875rem;
  font-size: 0.9375rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  transition: all var(--transition-base);
  background-color: white;
}

.form-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-input:hover:not(:focus) {
  border-color: var(--color-text-light);
}

.form-textarea {
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
}

.input-error {
  border-color: #ef4444 !important;
}

.error-text {
  font-size: 0.75rem;
  color: #ef4444;
  margin-top: 0.375rem;
}

/* チェックボックスラベル */
.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  margin-bottom: 0;
}

.form-checkbox {
  width: 1.125rem;
  height: 1.125rem;
  cursor: pointer;
  accent-color: var(--primary-color);
}

.form-group-indent {
  margin-left: 1.5rem;
}

.reservation-document-fields {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.35fr);
  gap: 0.75rem;
  margin-left: 1.5rem;
}

.reservation-document-fields .form-group {
  margin-bottom: 1rem;
}

.checkbox-label span {
  font-weight: 500;
}

.form-hint {
  font-size: 0.875rem;
  color: var(--color-text-muted);
  margin-top: 0.25rem;
}

/* 住所グループ */
.address-group {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.address-input-row {
  display: flex;
  gap: 0.625rem;
  align-items: stretch;
}

.address-input-row :deep(.google-place-input) {
  flex: 1 1 auto;
  min-width: 0;
  z-index: 2;
}

.map-confirm-link {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  flex-shrink: 0;
  min-width: 44px;
  min-height: 44px;
  padding: 0.625rem 0.8rem;
  border: 1px solid var(--primary-color);
  border-radius: var(--radius-md);
  color: #46623a;
  font-size: 0.8rem;
  font-weight: 600;
  text-decoration: none;
}

.map-confirm-link:hover {
  background: #46623a;
  border-color: #46623a;
  color: #ffffff;
}

.map-confirm-link:focus-visible {
  outline: 2px solid #46623a;
  outline-offset: 2px;
}

/* メンバー選択 */
.members-selection {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.member-item {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.5rem;
  border-radius: var(--radius-md);
  transition: background-color var(--transition-base);
}

.member-item:hover {
  background-color: var(--color-cool-bg);
}

.member-item input[type="checkbox"] {
  width: 1.125rem;
  height: 1.125rem;
  cursor: pointer;
  accent-color: var(--primary-color);
}

.member-item label {
  cursor: pointer;
  flex: 1;
  margin: 0;
  font-weight: 500;
}

/* エラーメッセージ */
.error-message {
  margin: 1.5rem 1.5rem 0;
  padding: 0.875rem 1rem;
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border-left: 3px solid #ef4444;
  border-radius: var(--radius-md);
  color: #991b1b;
  font-size: 0.875rem;
  font-weight: 500;
}

.rate-panel {
  margin: -0.25rem 0 1.25rem;
  padding: 0.75rem;
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  background: var(--color-background);
}

.rate-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.625rem;
  color: var(--color-text-muted);
  font-size: 0.875rem;
}

.rate-summary strong {
  color: var(--color-text);
}

.rate-controls {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: 0.5rem;
}

.rate-btn {
  padding: 0.625rem 0.8rem;
  font-size: 0.875rem;
  white-space: nowrap;
}

/* モーダルフッター */
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.75rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--color-border-light);
  flex-shrink: 0;
}

/* トランジション */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .modal-container {
  animation: modal-slide-up 0.3s ease-out;
}

.modal-fade-leave-active .modal-container {
  animation: modal-slide-down 0.3s ease-out;
}

@keyframes modal-slide-down {
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
}

/* レスポンシブ */
/* モバイル (～640px) */
@media (max-width: 640px) {
  .modal-overlay {
    padding: 0.5rem;
    align-items: flex-start;
  }

  .modal-container {
    max-width: 100%;
    width: 100%;
    max-height: calc(100dvh - 1rem);
    margin: 0;
  }

  .modal-header {
    padding: 1rem;
  }

  .modal-title {
    font-size: 1.125rem;
  }

  .close-button {
    width: 32px;
    height: 32px;
    font-size: 1.5rem;
  }

  .modal-body {
    padding: 1rem;
  }

  .form-section + .form-section {
    margin-top: 1.25rem;
  }

  .section-title {
    font-size: 0.9375rem;
    margin-bottom: 0.75rem;
  }

  .form-group {
    margin-bottom: 1rem;
  }

  .form-label {
    font-size: 0.8125rem;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .reservation-document-fields {
    grid-template-columns: 1fr;
    margin-left: 0;
  }

  .form-row-3 {
    grid-template-columns: 1fr;
  }

  .address-input-row {
    flex-direction: column;
  }

  input[type="text"],
  input[type="date"],
  input[type="time"],
  input[type="number"],
  input[type="url"],
  textarea,
  select {
    padding: 0.625rem 0.875rem;
    font-size: 1rem; /* iOS zoom 防止 */
  }

  .input-with-icon {
    gap: 0.5rem;
  }

  .input-icon {
    font-size: 1.125rem;
  }

  .time-inputs {
    gap: 0.75rem;
  }

  .category-buttons {
    gap: 0.5rem;
  }

  .category-btn {
    padding: 0.625rem 0.875rem;
    font-size: 0.875rem;
  }

  .member-grid {
    gap: 0.5rem;
  }

  .member-item {
    padding: 0.625rem 0.75rem;
    font-size: 0.875rem;
  }

  .error-message {
    margin: 1rem 1rem 0;
    padding: 0.75rem 0.875rem;
    font-size: 0.8125rem;
  }

  .modal-footer {
    gap: 0.5rem;
    margin-top: 1.25rem;
    padding-top: 1rem;
  }

  .modal-footer .btn {
    font-size: 0.875rem;
    padding: 0.625rem 1rem;
  }
}

/* タブレット (641px～1024px) */
@media (min-width: 641px) and (max-width: 1024px) {
  .modal-container {
    max-width: 650px;
  }
}
</style>
